import { useState } from 'react';
import { useConfig } from '@common/Hooks/useConfig';
import { useTranslation } from '@common/Hooks/useTranslation';
import { GA_EVENT_MOBILE_NAVIGATION_CLICK } from '@common/Tracking';
import { menuSelectEntryTracking, menuSelectItemListTracking } from '@common/Tracking/Navigation';
import { clearCachedData } from '@helpers/cache';
import { pushGAEventToDataLayer } from '@helpers/dataLayer';
import { formatLanguages } from '@helpers/formatLanguages';
import { languageSelectId } from '../constants/languageSelect';
import { storeSelectId } from '../constants/storeSelect';
import { useStoreInit } from './useStoreInit';

const defaultView = 'default';

export const useBurgerNavHandlers = (
    pageUuid, currentNav, navigationPath, storeSelectClick,
    languageSelectClick, languages, navItemClick, menuContainerRef,
    setIsNavOpen, selectedDepartment, setSelectedDepartment
) => {
    const [view, setView] = useState(defaultView);
    const [navClicked, setNavClicked] = useState('');
    const config = useConfig();
    const stores = useStoreInit();
    const translate = useTranslation();

    const handleViewChange = (newView, event) => {
        setView(newView);
        menuContainerRef.current.scrollTop = 0;

        if (event) {
            event.preventDefault();
        }
    };

    // this handler is passed to the child component (BackButton)
    // so it can update the state of the parent (Menu)
    const backButtonClick = () => {
        // remove last navigation uuid from state path
        navigationPath.splice(-1, 1);

        setNavClicked('');
        navItemClick(navigationPath, true);
        handleViewChange(defaultView);
    };

    const setStoreSelectMenu = () => {
        storeSelectClick({
            navId: storeSelectId,
            currentNavItem: {
                id: storeSelectId,
                name: translate('navigation.headline.shipping'),
                lvl: 2,
                children: stores
            },
            navigationPath: []
        });
    };

    const setLanguageSelectMenu = () => {
        languageSelectClick({
            navId: languageSelectId,
            currentNavItem: {
                id: languageSelectId,
                name: 'Navigation Language',
                lvl: 2,
                children: formatLanguages(languages[config.store])
            },
            navigationPath: []
        });
    };

    const handleStoreSelectClick = (event) => {
        setNavClicked(event.currentTarget.dataset.uuid);
        setStoreSelectMenu();
    };

    const handleLanguageSelectClick = (event) => {
        setNavClicked(event.currentTarget.dataset.uuid);
        setLanguageSelectMenu();
    };

    const menuItemClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        menuContainerRef.current.scrollTop = 0;

        const navId = event.currentTarget.dataset.uuid;

        if (navId !== undefined && navClicked !== storeSelectId && navClicked !== languageSelectId) {
            setNavClicked(navId);
        }

        const clickedNavHref = event.currentTarget.getAttribute('href');

        if (navId === storeSelectId) {
            handleStoreSelectClick(event);
        } else if (navId === languageSelectId) {
            handleLanguageSelectClick(event);
        } else if (navClicked === storeSelectId && navId === pageUuid) {
            window.location.href = `${App.Config.urlPrefix}${clickedNavHref}`;
        } else {
            const clickedNavItem = currentNav.children[navId];

            if (clickedNavItem) {
                const clickedNavItemChildren = clickedNavItem.children;
                const clickedNavItemLvl = clickedNavItem.lvl;

                if (clickedNavItem.lvl === 1) {
                    pushGAEventToDataLayer(GA_EVENT_MOBILE_NAVIGATION_CLICK, clickedNavItem.name, 'No sub category');
                    const newSelectedDepartment = {
                        id: clickedNavItem.navigationId,
                        name: clickedNavItem.languageContext.en?.name
                    };
                    setSelectedDepartment(newSelectedDepartment);
                    menuSelectEntryTracking(clickedNavItem, newSelectedDepartment);
                } else if (clickedNavItem.lvl === 2) {
                    pushGAEventToDataLayer(GA_EVENT_MOBILE_NAVIGATION_CLICK, currentNav.name, clickedNavItem.name);
                    menuSelectEntryTracking(clickedNavItem, selectedDepartment);
                }

                if (clickedNavItemLvl >= 4 || Object.keys(clickedNavItemChildren).length === 0) {
                    menuSelectItemListTracking(clickedNavItem, selectedDepartment);

                    if (window.location.pathname !== clickedNavHref.replace(/[?|#].*/, '')) {
                        if (navId === storeSelectId) {
                            clearCachedData();
                        }

                        window.location.href = clickedNavHref;
                    } else {
                        setIsNavOpen(false);
                    }

                    return;
                }
            } else if (clickedNavHref) {
                window.location.href = clickedNavHref;
                return;
            }

            // track menuItem click

            // push new navigation uuid into state path
            navigationPath.push(navId);

            // update currentTree state (re-render)
            navItemClick(navigationPath);
            menuContainerRef.current.scrollTop = 0;
        }
    };

    const handleLogin = () => {
        handleViewChange(defaultView);
    };

    return [
        navClicked, view, handleViewChange, backButtonClick, handleStoreSelectClick, handleLanguageSelectClick,
        menuItemClick, handleLogin
    ];
};
